import {
  Box,
  Button,
  createTheme,
  FormControl,
  IconButton,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ThemeProvider } from "@mui/material/styles"
import HelpIcon from "@mui/icons-material/Help";
import {
  Autocomplete,
  Chip,
  CircularProgress,
  Grid,
  TextField,
  Tooltip,
} from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import React, { useEffect, useState } from "react";
import FieldNames from "../../../defaults/FieldNames.json";
import AuditLogService from "../../../services/AuditLogService";
import CarryoverService from "../../../services/CarryoverService";
import ErrorService from "../../../services/ErrorService";
import FeatureService from "../../../services/FeatureService";
import {
  calculateFiscalYearQuarter,
  formatDateForDisplay,
  getSaveableDate,
  handleCarryoverTargetInitDateChange,
} from "../../../utils/DateHelper";
import BasicTextField from "../../details/customPanels/BasicTextField";
import InFormGenericModal from "../../InFormGenericModal";
import WorkflowFiscalYearFilter from "../WorkflowFiscalYearFilter/WorkflowFiscalYearFilter";
import {useSelector} from "react-redux";

const BlueAlien = "#1976d2";

export default function CarryoverEntryPopup({
  carryoverEditorOpen,
  setCarryoverEditorOpen,
  carryoverReasonOptions,
  setCarryoverReasonOptions,
  currentRow,
  presetInitationFiscalYear, 
  saveCallback
}) {
  const useStyles = makeStyles({
    underline: {
      "&&&:before": {
        borderBottom: "2px solid red",
      },
      "&&:after": {
        borderBottom: "2px solid red",
      },
    },
    condensedFormControl: {
      margin: "5px 15px 5px 15px",
      minWidth: 120,
      width: "90%",
    },
    formControl: {
      margin: "10px 15px 10px 15px",
      minWidth: 120,
      width: "90%",
    },
    formControlHeader: {
      margin: "0px 15px 0px 15px",
      minWidth: 120,
      width: "90%",
    },
    snackBarStyle: {
      top: "120px !important",
    },
  });

  const useErrorStyles = makeStyles({
    underline: {
      "&&&:before": {
        borderBottom: "2px solid red",
      },
      "&&:after": {
        borderBottom: "2px solid red",
      },
    },
  });

  const classes = useStyles();
  const errorClasses = useErrorStyles();

  const theme = createTheme({
    overrides: {
      palette: {
        red: {
          main: "#FF0000",
          light: "#FF0000",
          dark: "#FF0000",
          contrastText: "#242105",
        },
      },
      MuiPaper: {
        root: {
          backgroundColor: "green",
        },
      },
      MuiOutlinedInput: {
        root: {
          "& $notchedOutline": {
            //initial border
            borderColor: BlueAlien,
          },
          "&:hover $notchedOutline": {
            //border on hover
            borderColor: BlueAlien,
            border: "2px solid",
          },
          "&$focused $notchedOutline": {
            //border on focus
            borderColor: BlueAlien,
            border: "2px solid",
          },
        },
      },
    },
  });

  const userInfo = useSelector((state) => state.globalContext.userInfo);
  
  // State Variables
  const currentInitiationDate = currentRow
    ? currentRow[FieldNames.featureFields.initiationDate]
    : null;
  const [targetInitiationDateValue, setTargetInitiationDateValue] =
    useState(null);
  const featureInitiationFY = currentRow?.[FieldNames.featureFields.initiationFiscalYear];
  const featureCompletionFY = currentRow?.[FieldNames.featureFields.completionFiscalYear];
  const fiscalYearSelection = presetInitationFiscalYear ?? featureCompletionFY ?? featureInitiationFY;
  const [isSelectedFiscalYearInFuture,setIsSelectedFiscalYearInFuture] = useState(false);
  

  const [carryoverReasonSelection, setCarryoverReasonSelection] = useState("");
  const [carryoverReasonOpen, setCarryoverReasonOpen] = useState(false);
  const carryoverReasonLoading =
    carryoverReasonOpen && carryoverReasonOptions.length === 0;

  const [carryoverReasoning, setCarryoverReasoning] = useState("");

  const [
    carryoverTargetFiscalYearOptions,
    setCarryoverTargetFiscalYearOptions,
  ] = useState([]);
  const [
    carryoverTargetFiscalYearSelection,
    setCarryoverTargetFiscalYearSelection,
  ] = useState(null);
  const [currentError, setCurrentError] = useState("");
  const [toShowError, setToShowError] = useState(false);

  // Logic

  useEffect(() => {
    setCarryoverReasoning("");
    setCarryoverReasonSelection("");
  }, [carryoverEditorOpen]);

  useEffect(() => {
    let active = true;

    if (!carryoverReasonLoading) {
      return undefined;
    }

    (async () => {
      if (active) {
        setCarryoverReasonOptions([]);

        CarryoverService.getCarryoverReasons().then((response) => {
          setCarryoverReasonOptions(response);
        });
      }
    })();

    return () => {
      active = false;
    };
  }, [carryoverReasonLoading]);

  useEffect(() => {
    if (
      !fiscalYearSelection ||
      !fiscalYearSelection[FieldNames.carryoverFields.fiscalYear]
    ) {
      return;
    }
    let targetFyList = [];
    let futureTargetFyList = [];
    let currFY = calculateFiscalYearQuarter(new Date(), true);
    let selectedFy = Number(
      fiscalYearSelection[FieldNames.carryoverFields.fiscalYear]
    );
    if (selectedFy) {
      if (selectedFy > currFY) {
        setIsSelectedFiscalYearInFuture(true);
      } else {
        setIsSelectedFiscalYearInFuture(false);
      }
      for (let i = 1; i <= 5; i++) {
        futureTargetFyList.push(selectedFy + i);
      }
    }

    targetFyList = futureTargetFyList;
    targetFyList = targetFyList.map((year) => {
      return { FiscalYear: `${year}` };
    });
    setCarryoverTargetFiscalYearOptions(targetFyList);
    setCarryoverTargetFiscalYearSelection({ FiscalYear: `${selectedFy + 1}` });
  }, [fiscalYearSelection]);

  const closeCarryoverEditor = () => {
    setCarryoverEditorOpen(false);
    setCarryoverReasonSelection("");
    setCarryoverReasoning("");
    setTargetInitiationDateValue(null);
  };

  const saveCarryoverValue = () => {
    let currentFySelection = Number(
      fiscalYearSelection[FieldNames.carryoverFields.fiscalYear]
    );
    let targetFySelection = Number(
      carryoverTargetFiscalYearSelection[FieldNames.carryoverFields.fiscalYear]
    );

    let newCarryoverObj = {
      JoinID: currentRow[FieldNames.featureFields.joinID],
      JoinFeature: currentRow[FieldNames.featureFields.joinFeature],
      EntityID:
        currentRow[FieldNames.featureFields.entityID] ??
        currentRow[FieldNames.featureFields.objectID],
      EntityOBJECTID: currentRow[FieldNames.featureFields.objectID],
      EntityType: currentRow[FieldNames.featureFields.entityType],
      Reason: carryoverReasonSelection,
      CurrentFiscalYear: currentFySelection,
      TargetFiscalYear: targetFySelection,
      CreatedBy: userInfo[FieldNames.userFields.nifcUsername],
      CreatedOnDate: getSaveableDate(new Date()),
      ModifiedBy: userInfo[FieldNames.userFields.nifcUsername],
      ModifiedDate: getSaveableDate(new Date()),
      IsArchived: FieldNames.yesNo.no,
    };

    let featureObj = {
      OBJECTID: currentRow[FieldNames.featureFields.entityID],
      ID: currentRow[FieldNames.featureFields.id],
      IsCarryover: FieldNames.yesNo.yes,
      CarryoverFiscalYear: targetFySelection,
      InitiationDate: getSaveableDate(targetInitiationDateValue),
      InitiationFiscalYear: targetFySelection,
      InitiationFiscalQuarter: calculateFiscalYearQuarter(
        new Date(targetInitiationDateValue),
        false
      ),
    };

    if (!currentRow[FieldNames.featureFields.originalInitiationDate]) {
      // Set original initiation date values if they have not been set before
      featureObj[FieldNames.featureFields.originalInitiationDate] =
        getSaveableDate(currentRow[FieldNames.featureFields.initiationDate]);
      featureObj[FieldNames.featureFields.originalInitiationFiscalYear] =
        currentRow[FieldNames.featureFields.initiationFiscalYear];
      featureObj[FieldNames.featureFields.originalInitiationFiscalQuarter] =
        currentRow[FieldNames.featureFields.initiationFiscalQuarter];
    }

    let baseMessage = `Action was marked as carryover from FY${currentFySelection} to FY${targetFySelection} for the following reason: ${carryoverReasonSelection}`;
    let auditLogMessage = carryoverReasoning
      ? baseMessage + ". " + carryoverReasoning
      : baseMessage;
    FeatureService.updateFeatureAttributes([featureObj])
      .then((resp) => {
        if (resp?.updateResults?.length > 0 && resp?.updateResults[0].success) {
          CarryoverService.addCarryoverEntry(newCarryoverObj)
            .then((resp) => {
              AuditLogService.addAuditLogEntry(
                currentRow,
                currentRow[FieldNames.featureFields.joinID],
                currentRow[FieldNames.featureFields.joinFeature],
                userInfo[FieldNames.userFields.nifcUsername],
                auditLogMessage
              )
                .then((response) => {
                  if(saveCallback)
                  {
                    saveCallback();
                  }
                  setCarryoverReasoning("");
                  setTargetInitiationDateValue(null);
                })
                .catch((error) => {
                  ErrorService.addErrorLogEntry(
                    error
                  );
                });
            })
            .catch((error) => {
              ErrorService.addErrorLogEntry(
                error
              );
            });
        }
      })
      .catch((error) => {
        ErrorService.addErrorLogEntry(
          error
        );
      });
  };

  return (
    <InFormGenericModal
      openFlag={carryoverEditorOpen}
      closeMethod={closeCarryoverEditor}
      titleText={"Edit Carryover"}
      displayButton={false}
    >
      <Box
        style={{
          maxHeight: "300px",
          overflow: "auto",
          width: "100%",
          minWidth: "450px",
        }}
      >
        <FormControl variant="standard" className={classes.formControl}>
          <ThemeProvider theme={theme}>
            <Grid
              item
              size={12}
              container
              direction="row"
              className="grid-item"
              style={{ alignItems: "center" }}
            >
              <Grid item size={12}>
                <Typography display={"inline"}>Carryover Reason: </Typography>
                <Typography display={"inline"} color={"red"}>
                  {" "}
                  *
                </Typography>
              </Grid>
              <Grid item size={12}>
                <Autocomplete
                  //multiple
                  disableClearable
                  id="budgetBucketMulti"
                  open={carryoverReasonOpen}
                  value={carryoverReasonSelection}
                  onChange={(event, newValue) => {
                    if (newValue === "N/A") {
                      newValue = "";
                    }
                    setCarryoverReasonSelection(newValue);
                  }}
                  onOpen={() => {
                    setCarryoverReasonOpen(true);
                  }}
                  onClose={() => {
                    setCarryoverReasonOpen(false);
                  }}
                  isOptionEqualToValue={(option, value) => {
                    return option === value || value === "";
                  }}
                  getOptionLabel={(option) => {
                    return option;
                  }}
                  options={carryoverReasonOptions}
                  getOptionDisabled={(option) => {
                    if (carryoverReasonSelection) {
                      const selected = carryoverReasonSelection;
                      if (selected && option && selected === option) {
                        return true;
                      } else {
                        return false;
                      }
                    } else {
                      return false;
                    }
                  }}
                  loading={carryoverReasonLoading}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        disabled={false}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {carryoverReasonLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params?.InputProps?.endAdornment}
                            </React.Fragment>
                          ),
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item size={8}>
                <Typography display={"inline"}>
                  Current Initiation Date:{" "}
                </Typography>
                <Typography display={"inline"} fontWeight={"bold"}>
                  {currentRow
                    ? formatDateForDisplay(
                          currentRow[FieldNames.featureFields.initiationDate]
                      )
                    : "Err"}
                </Typography>
              </Grid>
              <Grid item size={12}>
                <Typography display={"inline"}>
                  Target Initiation Date:{" "}
                </Typography>
                <Typography display={"inline"} color={"red"}>
                  {" "}
                  *
                </Typography>
              </Grid>
              <Grid item size={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    format="MM/dd/yyyy"
                    fullWidth
                    clearable
                    clearText="Clear"
                    {...(!targetInitiationDateValue
                      ? { InputProps: { errorClasses } }
                      : { InputProps: {} })}
                    id={`datePicker-date`}
                    value={targetInitiationDateValue}
                    onChange={(value) =>
                      handleCarryoverTargetInitDateChange(
                        value,
                        setTargetInitiationDateValue,
                        setCurrentError,
                        setToShowError,
                        setCarryoverTargetFiscalYearSelection,
                        currentRow
                      )
                    }
                    slotProps={{
                      textField: {
                        helperText: toShowError ? currentError ?? "" : "",
                        error: toShowError
                      },
                    }}
                    minDate={currentRow ? currentInitiationDate : null}
                    onError={(reason, value) => {
                      switch (reason) {
                        case "minDate":
                          setToShowError(true);
                          setCurrentError(
                            `Selected date should not be before the existing initation date ${formatDateForDisplay(new Date())}`
                          );
                          break;
                        case null:
                          setToShowError(false);
                          setCurrentError("");
                          break;
                        default:
                          break;
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item size={12}>
                <Typography display={"inline"}>Target Fiscal Year: </Typography>
                <Typography display={"inline"} color={"red"}>
                  {" "}
                  *
                </Typography>
                <Tooltip
                  title={`Value is derived from the chosen initiation date`}
                >
                  <IconButton style={{ padding: 0 }} aria-label="Help">
                    <HelpIcon
                      fontSize="small"
                      color="primary"
                      style={{ right: "0px" }}
                    />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item size={12}>
                <WorkflowFiscalYearFilter
                  fiscalYearSelection={carryoverTargetFiscalYearSelection}
                  setFiscalYearSelection={setCarryoverTargetFiscalYearSelection}
                  fiscalYearOptions={carryoverTargetFiscalYearOptions}
                  hideFiscalYearHeader={true}
                  isDisabled={true}
                />
              </Grid>
            </Grid>
            <Grid
              item
              size={12}
              container
              direction="row"
              className="grid-item"
              style={{ alignItems: "center" }}
            >
              <Grid item size={12}>
                <BasicTextField
                  field="carryoverReason"
                  label=""
                  setValue={setCarryoverReasoning}
                  variant={"outlined"}
                  placeholder={"Enter notes for carryover"}
                  useFullWidth={true}
                />
              </Grid>
            </Grid>
          </ThemeProvider>
        </FormControl>
        <Box sx={{ marginLeft: "60%", marginBottom: "5px" }}>
          <Button
            variant="outlined"
            onClick={() => {
              setCarryoverReasonSelection("");
              setCarryoverReasoning("");
              setTargetInitiationDateValue(null);
              setCarryoverEditorOpen(false);
            }}
            aria-label="cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              saveCarryoverValue();
              setCarryoverEditorOpen(false);
            }}
            disabled={
              !carryoverTargetFiscalYearSelection || !carryoverReasonSelection
            }
            aria-label="edit"
            sx={{ marginLeft: "5px" }}
          >
            Save
          </Button>
        </Box>
      </Box>
    </InFormGenericModal>
  );
}
